import { useMemo } from 'react';

import { Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid';

import { StatusChip } from '@/components/common/StatusChip';
import type { DateRangePreset, ScheduleSummary, TriggerDetail } from '@/types/schedule';

import {
  formatDateForDisplay,
  getDateRangeFromPreset,
  getScheduledDateRangePresets,
} from '../utils/dateRanges';
import { formatExecutionHistoryDateTime } from '../utils/scheduleDateTime';

interface PlannedUpcomingGridProps {
  details: TriggerDetail[];
  isLoading?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationChange: (model: GridPaginationModel) => void;
  dateRangePreset: DateRangePreset;
  onDateRangeChange: (preset: DateRangePreset) => void;
  schedules?: ScheduleSummary[];
}

export const PlannedUpcomingGrid = ({
  details,
  isLoading = false,
  paginationModel,
  onPaginationChange,
  dateRangePreset,
  onDateRangeChange,
  schedules = [],
}: PlannedUpcomingGridProps) => {
  const dateRangePresets = getScheduledDateRangePresets();
  const currentRange = getDateRangeFromPreset(dateRangePreset);

  const rowsWithId = useMemo(
    () =>
      details.map((detail, index) => ({
        ...detail,
        id: `scheduled-${detail.baseId}-${detail.executionTime}-${index}`,
      })),
    [details]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'executionTime',
        headerName: 'Scheduled Date/Time (UTC)',
        width: 220,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as TriggerDetail;
          return (
            <Typography variant="body2">
              {params.value ? formatExecutionHistoryDateTime(row, schedules) : '-'}
            </Typography>
          );
        },
      },
      {
        field: 'transformerName',
        headerName: 'Recipe',
        flex: 1,
        minWidth: 180,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" noWrap>
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        field: 'environmentName',
        headerName: 'Environment',
        width: 140,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => (
          <Chip label={params.value || '-'} size="small" color="secondary" variant="outlined" />
        ),
      },
      {
        field: 'executionStatus',
        headerName: 'Status',
        width: 120,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => (
          <StatusChip status={params.value || 'Scheduled'} />
        ),
      },
    ],
    [schedules]
  );

  const handleDateRangeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPreset: DateRangePreset | null
  ) => {
    if (newPreset !== null) {
      onDateRangeChange(newPreset);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Upcoming Schedules (UTC)
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <ToggleButtonGroup
            value={dateRangePreset}
            exclusive
            onChange={handleDateRangeChange}
            size="small"
          >
            {dateRangePresets.map((preset) => (
              <ToggleButton key={preset.value} value={preset.value}>
                {preset.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Showing upcoming schedules from {formatDateForDisplay(currentRange.startDate)} to{' '}
        {formatDateForDisplay(currentRange.endDate)} — projected from active schedules, not
        execution history.
      </Typography>
      <DataGrid
        rows={rowsWithId}
        columns={columns}
        loading={isLoading}
        paginationMode="client"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationChange}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    </Box>
  );
};
